import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { configureStore } from '@reduxjs/toolkit';
import { FLUSH, PAUSE, PERSIST, persistReducer, persistStore, PURGE, REGISTER, REHYDRATE } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { allGamesFiltersReducer } from './slices/allGamesFilterSlice';
import type { GamesFilterInitialState } from './slices/consts';
import { homeScreenAllGamesFiltersReducer } from './slices/homeAllGamesFilterSlice';
import { myGamesFiltersReducer } from './slices/myGamesFiltersSlice';
import { pvpAllGamesFiltersReducer } from './slices/pvpAllGamesFiltersSlice';
import { pvpMyGamesFiltersReducer } from './slices/pvpMyGamesFiltersSlice';
import { teamReducer } from './slices/teamSlice';

// Конфигурация persist для команд (по аналогии со старой реализацией localStorage)
const teamPersistConfig = {
  key: 'team',
  storage,
  // Персистим только кеш команд (несохраненные команды)
  whitelist: ['cachedTeams'],
  // Трансформируем данные перед сохранением - оставляем только команды без id
  transforms: [
    {
      // Трансформ для сохранения только команд без id (draft команды)
      in: (inboundState: any) => {
        if (!inboundState?.cachedTeams) return inboundState;

        // Фильтруем команды - сохраняем только без id (как в старой реализации)
        const draftTeams = Object.fromEntries(
          Object.entries(inboundState.cachedTeams).filter(([, team]: [string, any]) => team && !team.id),
        );

        return {
          ...inboundState,
          cachedTeams: draftTeams,
        };
      },
      out: (outboundState: any) => outboundState,
    },
  ],
};

const persistedTeamReducer = persistReducer(teamPersistConfig, teamReducer);

export const store = configureStore({
  reducer: {
    homeScreenAllGamesFilters: homeScreenAllGamesFiltersReducer,
    pvpAllGamesFilters: pvpAllGamesFiltersReducer,
    pvpMyGamesFilters: pvpMyGamesFiltersReducer,
    myGamesFilters: myGamesFiltersReducer,
    allGamesFilters: allGamesFiltersReducer,
    team: persistedTeamReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;

export type GamesFilterReducerName = {
  [Name in keyof RootState]: RootState[Name] extends GamesFilterInitialState ? Name : never;
}[keyof RootState];

export type AppDispatch = typeof store.dispatch;

export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export const useAppDispatch = () => useDispatch<AppDispatch>();
